// ✅ src/pages/Sales/PartPaymentEntry.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS)
// View mode rules:
// - If URL has ?id= or ?custid= or ?customerId=  => VIEW ONLY
// - Disable all inputs
// - Hide Save button
// - Show Payment Records with Print per row

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaHistory, FaPrint } from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { useLocation, useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

// ✅ safe numeric text (fixes "1.2 acre" etc)
function toNumberText(v) {
  const s = String(v ?? "").trim();
  const m = s.match(/-?\d+(\.\d+)?/);
  return m ? m[0] : "";
}

function formatINR(amount) {
  const n = Number(amount || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

const PartPaymentEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ VIEW ONLY support
  const viewCustId = query.get("id") || query.get("custid") || query.get("customerId") || "";
  const isView = !!viewCustId;

  // -------------------------
  // Form state (includes enquiryNumber)
  // -------------------------
  const [formData, setFormData] = useState({
    enquiryNumber: "",

    customerId: "",
    customerName: "",
    leadSource: "",
    productName: "",
    salesPerson: "",
    fullAddress: "",
    mobileNumber: "",
    totalSqFeet: "",
    plotNoAssetCode: "",
    assetClass: "",
    location: "",
    address: "",
    propertyInNameOf: "",
    dateOfAcquisition: "",
    valueOfAsset: "",
    billedAmount: "",
    payingAmount: "",
    nextPaymentDate: "",
    paymentMode: "Cash",
    remarks: "",
    balanceAmount: "",
    date: "",
    balanceAmountAfterPayment: "",
  });

  const [customerIds, setCustomerIds] = useState([]);
  const [plotNumbers, setPlotNumbers] = useState([]);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // -------------------------
  // API endpoints
  // -------------------------
  const api = {
    customers: `${BASE_URL}/api/partEntry/customers`,
    assetCodes: `${BASE_URL}/api/partEntry/assetCodes`,
    assetDetails: `${BASE_URL}/api/partEntry/customerAssetDetails`,
    paymentDetails: `${BASE_URL}/api/partEntry/paymentRecords`,
    billno: `${BASE_URL}/api/partEntry/billno`,
    insert: `${BASE_URL}/api/partEntry/insert`,
    markPaid: `${BASE_URL}/api/partEntry/markPaid`,
  };

  // -------------------------
  // ✅ setField
  // In VIEW mode: block changes
  // -------------------------
  const setField = (field, value) => {
    if (isView) return;

    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "payingAmount" || field === "balanceAmount" || field === "billedAmount") {
        const bal = Number(toNumberText(next.balanceAmount) || 0);
        const paying = Number(toNumberText(next.payingAmount) || 0);
        next.balanceAmountAfterPayment = String(Math.max(0, bal - paying));
      }

      return next;
    });
  };

  // -------------------------
  // Loaders
  // -------------------------
  const loadCustomers = async () => {
    const res = await axios.get(api.customers, {
      params: { type: "Part Payment" },
      headers: commonHeaders,
    });
    setCustomerIds(res.data?.data || []);
  };

  const loadAssetCodes = async () => {
    const res = await axios.get(api.assetCodes, {
      headers: commonHeaders,
    });
    setPlotNumbers(res.data?.data || []);
  };

  // details from tbl_Sales_Leadconverted_Mas
  const loadCustomerAssetDetails = async (customerId) => {
    if (!customerId) return;

    const res = await axios.get(api.assetDetails, {
      params: { customerId },
      headers: commonHeaders,
    });

    const d = res.data?.data;
    if (!d) return;

    const purchaseValue = toNumberText(d.fld_PurchaseValue ?? d.fld_purchasevalue ?? "");
    const enq =
      d.fld_enquirnum ??
      d.fld_enquiryno ??
      d.fld_enquirynum ??
      d.fld_enquiryNumber ??
      d.fld_enquiry_no ??
      "";

    setFormData((prev) => {
      const next = {
        ...prev,
        enquiryNumber: String(enq ?? ""),

        customerId: d.fld_customerid ?? prev.customerId,
        customerName: d.fld_customername ?? prev.customerName,
        leadSource: d.fld_leadsource ?? prev.leadSource,
        productName: d.fld_Productname ?? d.fld_productname ?? prev.productName,
        salesPerson: d.fld_salesperson ?? prev.salesPerson,
        fullAddress: d.fld_address ?? d.fld_fulladdress ?? prev.fullAddress,
        mobileNumber: d.fld_mobile ?? d.fld_mobileno ?? prev.mobileNumber,
        totalSqFeet: toNumberText(d.fld_extend ?? d.fld_totalsqfeet ?? prev.totalSqFeet),

        plotNoAssetCode: d.fld_assetid ?? prev.plotNoAssetCode,
        assetClass: d.fld_assetclass ?? prev.assetClass,
        location: d.fld_location ?? prev.location,
        address: d.fld_address ?? prev.address,
        propertyInNameOf: d.fld_propertyname ?? prev.propertyInNameOf,
        dateOfAcquisition: d.fld_acquisitiondate
          ? String(d.fld_acquisitiondate).slice(0, 10)
          : prev.dateOfAcquisition,

        valueOfAsset: purchaseValue || toNumberText(prev.valueOfAsset),
        billedAmount: purchaseValue || toNumberText(prev.billedAmount),

        balanceAmount: purchaseValue || toNumberText(prev.balanceAmount),
      };

      const bal = Number(toNumberText(next.balanceAmount) || 0);
      const paying = Number(toNumberText(next.payingAmount) || 0);
      next.balanceAmountAfterPayment = String(Math.max(0, bal - paying));
      return next;
    });
  };

  // payment history + current balance from usp_sales_getpartpaymentbilldetails
  const loadPaymentDetails = async (custid) => {
    if (!custid) return;

    const res = await axios.get(api.paymentDetails, {
      params: { custid },
      headers: commonHeaders,
    });

    const rows = res.data?.data || [];

    setPaymentRecords(
      rows.map((r) => ({
        id: r.fld_paymentid,
        billno: r.fld_billno,
        date: r.fld_date,
        amount: Number(r.fld_payingamount || 0),
        paymentMode: r.fld_paymentmode,
        balanceAfter: Number(r.fld_balanceamount || 0),
        remarks: r.fld_remarks || "",
        status: r.fld_status,
        nextPaymentDate: r.fld_nextpaymentdate,

        customerId: r.fld_customerid,
        customerName: r.fld_customername,
        productName: r.fld_productname,
        location: r.fld_location,
      }))
    );

    const last = rows.length ? rows[rows.length - 1] : null;
    if (last) {
      const newBal = toNumberText(last.fld_balanceamount);
      setFormData((prev) => {
        const next = { ...prev, balanceAmount: newBal };
        const bal = Number(toNumberText(next.balanceAmount) || 0);
        const paying = Number(toNumberText(next.payingAmount) || 0);
        next.balanceAmountAfterPayment = String(Math.max(0, bal - paying));
        return next;
      });
    }
  };

  // -------------------------
  // ✅ PRINT: Pass whole customer + selected payment
  // -------------------------
  const handlePrintRow = (selectedPayment) => {
    navigate("/Sales/PartPaymentBillPrint", {
      state: {
        customer: formData,
        payment: selectedPayment,
        payments: paymentRecords,
      },
    });
  };

  // -------------------------
  // Initial load
  // -------------------------
  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadCustomers(), loadAssetCodes()]);

        if (isView) {
          setFormData((p) => ({ ...p, customerId: viewCustId }));
          await loadCustomerAssetDetails(viewCustId);
          await loadPaymentDetails(viewCustId);

          setFormData((p) => ({
            ...p,
            payingAmount: "",
            date: "",
            remarks: "",
            nextPaymentDate: "",
          }));
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load part payment entry data");
      }
    })();
  }, [isView, viewCustId]);

  // Add mode: on customer select -> load details + history
  useEffect(() => {
    (async () => {
      if (!isView && formData.customerId) {
        try {
          await loadCustomerAssetDetails(formData.customerId);
          await loadPaymentDetails(formData.customerId);
        } catch (e) {
          console.error(e);
          toast.error("Failed to load customer details");
        }
      }
    })();
  }, [formData.customerId, isView]);

  // -------------------------
  // Save (Insert PART)  (disabled in view mode)
  // -------------------------
  const validateForm = () => {
    const requiredFields = ["customerId", "customerName", "mobileNumber", "billedAmount", "payingAmount", "date"];
    for (const f of requiredFields) {
      if (!String(formData[f] ?? "").trim()) {
        toast.error(`Please fill in ${f.replace(/([A-Z])/g, " $1").toLowerCase()}`);
        return false;
      }
    }
    if (!String(formData.enquiryNumber ?? "").trim()) {
      toast.error("Enquiry Number not found. Please re-select customer.");
      return false;
    }
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(String(formData.mobileNumber))) {
      toast.error("Please enter a valid mobile number");
      return false;
    }
    const billed = Number(toNumberText(formData.billedAmount) || 0);
    const paying = Number(toNumberText(formData.payingAmount) || 0);
    const bal = Number(toNumberText(formData.balanceAmount) || 0);
    if (billed <= 0) return toast.error("Billed amount must be greater than 0"), false;
    if (paying <= 0) return toast.error("Paying amount must be greater than 0"), false;
    if (paying > bal) return toast.error("Paying amount cannot exceed balance amount"), false;
    return true;
  };

  const handleSave = async () => {
    if (isView) return;
    if (!validateForm()) return;

    setLoading(true);
    try {
      const billRes = await axios.get(api.billno, {
        headers: commonHeaders,
      });

      const maxNo = Number(billRes.data?.data?.fld_billno || 0);
      const newBillNo = String(maxNo + 1);

      const paying = Number(toNumberText(formData.payingAmount) || 0);
      const bal = Number(toNumberText(formData.balanceAmount) || 0);
      const after = Math.max(0, bal - paying);

      const payload = {
        customerid: formData.customerId,
        fld_assetid: formData.plotNoAssetCode,
        fld_enquiryno: formData.enquiryNumber,
        fld_location: formData.location || "",
        fld_paymode: "PART",
        fld_assetvalue: Number(toNumberText(formData.billedAmount) || 0),
        fld_payingamount: paying,
        fld_date: formData.date,
        fld_billno: newBillNo,
        fld_amounttype: formData.paymentMode,
        fld_createddate: new Date().toISOString(),
        fld_createdby: userId || "0",
        fld_ipaddress: "0.0.0.0",
        remarks: formData.remarks || "",
        balamount: after,
        nextduedate: formData.nextPaymentDate
          ? new Date(formData.nextPaymentDate).toISOString()
          : null,
      };

      await axios.post(api.insert, payload, {
        headers: commonHeaders,
      });

      if (after === 0) {
        await axios.put(
          api.markPaid,
          {
            customerId: formData.customerId,
            assetId: formData.plotNoAssetCode,
          },
          {
            headers: commonHeaders,
          }
        );
      }

      toast.success("Part payment saved successfully!");
      await loadPaymentDetails(formData.customerId);

      setFormData((prev) => ({
        ...prev,
        payingAmount: "",
        date: "",
        remarks: "",
        nextPaymentDate: "",
        balanceAmountAfterPayment: String(after),
      }));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isView) return;
    setFormData({
      enquiryNumber: "",
      customerId: "",
      customerName: "",
      leadSource: "",
      productName: "",
      salesPerson: "",
      fullAddress: "",
      mobileNumber: "",
      totalSqFeet: "",
      plotNoAssetCode: "",
      assetClass: "",
      location: "",
      address: "",
      propertyInNameOf: "",
      dateOfAcquisition: "",
      valueOfAsset: "",
      billedAmount: "",
      payingAmount: "",
      nextPaymentDate: "",
      paymentMode: "Cash",
      remarks: "",
      balanceAmount: "",
      date: "",
      balanceAmountAfterPayment: "",
    });
    setPaymentRecords([]);
    toast.info("Form reset successfully");
  };

  // -------------------------
  // UI helpers: disable / readonly
  // -------------------------
  const inputCls = (readonly = false) =>
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
      isDark
        ? readonly
          ? "bg-gray-600 border-gray-600 text-gray-200"
          : "bg-gray-700 border-gray-600 text-white"
        : readonly
        ? "bg-gray-100 border-gray-300 text-gray-700"
        : "bg-white border-gray-300 text-gray-900"
    }`;

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PART PAYMENT ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Part Payment Entry {isView ? "(VIEW ONLY)" : ""}
            </h1>

            {!isView && (
              <button
                onClick={handleReset}
                className={`px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                Reset
              </button>
            )}
          </div>

          {/* Enquiry */}
          <div className={`p-4 rounded-lg mb-6 border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`}>
            <label className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              Enquiry Number
            </label>
            <input value={formData.enquiryNumber} readOnly className={inputCls(true)} />
          </div>

          {/* Customer Info */}
          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              Customer Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Customer ID <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.customerId}
                    disabled={isView}
                    onChange={(e) => {
                      const cid = e.target.value;
                      setField("customerId", cid);
                      const found = customerIds.find((x) => String(x.fld_customerid) === String(cid));
                      if (found) setField("customerName", found.fld_customername);
                    }}
                    className={inputCls(isView)}
                  >
                    <option value="">Select</option>
                    {customerIds.map((x) => (
                      <option key={x.fld_customerid} value={x.fld_customerid}>
                        {x.fld_customerid}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={formData.customerName}
                    readOnly={isView}
                    onChange={(e) => setField("customerName", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Lead Source
                  </label>
                  <input
                    value={formData.leadSource}
                    readOnly={isView}
                    onChange={(e) => setField("leadSource", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Product Name
                  </label>
                  <input
                    value={formData.productName}
                    readOnly={isView}
                    onChange={(e) => setField("productName", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Sales Person
                  </label>
                  <input
                    value={formData.salesPerson}
                    readOnly={isView}
                    onChange={(e) => setField("salesPerson", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Full Address
                  </label>
                  <textarea
                    rows="3"
                    value={formData.fullAddress}
                    readOnly={isView}
                    onChange={(e) => setField("fullAddress", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={formData.mobileNumber}
                    readOnly={isView}
                    onChange={(e) => setField("mobileNumber", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Total Sq.Feet
                  </label>
                  <input
                    value={formData.totalSqFeet}
                    readOnly={isView}
                    onChange={(e) => setField("totalSqFeet", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Plot No / Asset Code
                  </label>
                  <select
                    value={formData.plotNoAssetCode}
                    disabled={isView}
                    onChange={(e) => setField("plotNoAssetCode", e.target.value)}
                    className={inputCls(isView)}
                  >
                    <option value="">Select</option>
                    {plotNumbers.map((x) => (
                      <option key={x.fld_assetid} value={x.fld_assetid}>
                        {x.fld_assetcode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Class
                  </label>
                  <input
                    value={formData.assetClass}
                    readOnly={isView}
                    onChange={(e) => setField("assetClass", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Location
                  </label>
                  <input
                    value={formData.location}
                    readOnly={isView}
                    onChange={(e) => setField("location", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Address
                  </label>
                  <input
                    value={formData.address}
                    readOnly={isView}
                    onChange={(e) => setField("address", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Property In the Name of
                  </label>
                  <textarea
                    rows="3"
                    value={formData.propertyInNameOf}
                    readOnly={isView}
                    onChange={(e) => setField("propertyInNameOf", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Date of Acquisition
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfAcquisition}
                    readOnly={isView}
                    onChange={(e) => setField("dateOfAcquisition", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Value of Asset
                  </label>
                  <input
                    value={formData.valueOfAsset}
                    readOnly={isView}
                    onChange={(e) => setField("valueOfAsset", e.target.value)}
                    className={inputCls(isView)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Records */}
          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-green-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-green-900"}`}>
              <FaHistory /> Payment Records
            </h3>

            {paymentRecords.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border shadow-lg">
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Bill No</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Payment Mode</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Balance After</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Print</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paymentRecords.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        }`}
                      >
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.billno}</td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.date}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {formatINR(r.amount)}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.paymentMode}</td>
                        <td
                          className={`px-4 py-3 text-sm font-semibold ${
                            Number(r.balanceAfter) > 0
                              ? isDark
                                ? "text-red-400"
                                : "text-red-600"
                              : isDark
                              ? "text-green-400"
                              : "text-green-600"
                          }`}
                        >
                          {formatINR(r.balanceAfter)}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.status}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handlePrintRow(r)}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold shadow ${
                              isDark
                                ? "bg-gray-700 hover:bg-gray-600 text-white"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                            }`}
                          >
                            <FaPrint /> Print
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={`text-center py-8 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                <FaHistory className="mx-auto text-4xl mb-4 opacity-50" />
                <p>No payment records found</p>
              </div>
            )}
          </div>

          {/* ✅ Save Button only in ADD mode */}
          {!isView && (
            <div className="flex justify-center">
              <button
                onClick={handleSave}
                disabled={loading}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSave />
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default PartPaymentEntry;